import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Clock, TrendingUp, Zap } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';

const SearchBar = ({ 
  value, 
  onChange, 
  onSearch, 
  placeholder = 'Search medical codes...', 
  suggestions = [],
  recentSearches = [],
  isLoading = false,
  disabled = false,
  searchType = 'cpt', // 'cpt' or 'icd'
  onSuggestionGenerate = null, // Callback to generate smart suggestions
  showAdvancedFilters = false,
  onToggleFilters = null
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Debounce the search value for smart suggestions
  const debouncedValue = useDebounce(value, 300);

  // Generate smart suggestions when debounced value changes
  useEffect(() => {
    if (debouncedValue && debouncedValue.length >= 2 && onSuggestionGenerate) {
      const newSuggestions = onSuggestionGenerate(searchType, debouncedValue);
      setSmartSuggestions(newSuggestions || []);
    } else {
      setSmartSuggestions([]);
    }
  }, [debouncedValue, searchType, onSuggestionGenerate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch();
      setShowSuggestions(false);
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion);
    onSearch();
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    onChange('');
    inputRef.current?.focus();
  };

  // Combine all suggestions with smart suggestions prioritized
  const allSuggestions = useMemo(() => {
    const combined = [];
    
    // Add smart suggestions first
    if (smartSuggestions.length > 0) {
      combined.push({
        type: 'smart',
        title: 'Smart Suggestions',
        icon: Zap,
        items: smartSuggestions.slice(0, 5)
      });
    }
    
    // Add recent searches
    if (recentSearches.length > 0) {
      combined.push({
        type: 'recent',
        title: 'Recent Searches',
        icon: Clock,
        items: recentSearches.slice(0, 3)
      });
    }
    
    // Add manual suggestions
    if (suggestions.length > 0) {
      combined.push({
        type: 'manual',
        title: 'Suggestions',
        icon: TrendingUp,
        items: suggestions.slice(0, 5)
      });
    }
    
    return combined;
  }, [smartSuggestions, recentSearches, suggestions]);

  const hasSuggestions = allSuggestions.length > 0;

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl mx-auto">
      <motion.div
        className={`relative flex items-center bg-white dark:bg-gray-800 border-2 rounded-xl shadow-sm transition-all duration-200 ${
          isFocused 
            ? 'border-blue-500 shadow-lg shadow-blue-500/20 dark:border-blue-400' 
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        animate={{ scale: isFocused ? 1.02 : 1 }}
        transition={{ duration: 0.2 }}
      >
        <Search className="absolute left-4 h-5 w-5 text-gray-400 dark:text-gray-500" />
        
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onFocus={() => {
            setIsFocused(true);
            setShowSuggestions(hasSuggestions);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-12 pr-20 py-4 text-lg bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:opacity-50"
        />

        <div className="absolute right-2 flex items-center space-x-2">
          {value && (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              onClick={clearSearch}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </motion.button>
          )}
          
          <motion.button
            onClick={onSearch}
            disabled={disabled || isLoading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            whileHover={{ scale: disabled ? 1 : 1.05 }}
            whileTap={{ scale: disabled ? 1 : 0.95 }}
          >
            {isLoading ? (
              <motion.div
                className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            ) : (
              'Search'
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* Enhanced Suggestions Dropdown */}
      <AnimatePresence>
        {showSuggestions && isFocused && hasSuggestions && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto"
          >
            {allSuggestions.map((section, sectionIndex) => (
              <div key={section.type} className={sectionIndex > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}>
                <div className="p-3">
                  <div className="flex items-center text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    <section.icon className={`h-3 w-3 mr-1 ${
                      section.type === 'smart' ? 'text-yellow-500' : 
                      section.type === 'recent' ? 'text-blue-500' : 'text-green-500'
                    }`} />
                    {section.title}
                    {section.type === 'smart' && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 rounded">
                        AI
                      </span>
                    )}
                  </div>
                  {section.items.map((item, index) => (
                    <motion.button
                      key={`${section.type}-${index}`}
                      onClick={() => handleSuggestionClick(item)}
                      className={`block w-full text-left px-2 py-2 text-sm rounded transition-colors ${
                        section.type === 'smart' 
                          ? 'text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 font-medium'
                          : section.type === 'recent'
                          ? 'text-gray-600 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                      whileHover={{ x: 4 }}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="flex items-center justify-between">
                        <span>{item}</span>
                        {section.type === 'smart' && (
                          <Zap className="h-3 w-3 text-yellow-500 opacity-60" />
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Search Tips */}
            <div className="border-t border-gray-100 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-700/50">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <div className="font-medium mb-1">Search Tips:</div>
                <div>• Use partial codes (e.g., "99213")</div>
                <div>• Search by procedure (e.g., "office visit")</div>
                <div>• Try medical terms (e.g., "injection")</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;
